import { useState } from "react";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { z } from "zod";
import { fetchUser, homeFor } from "../lib/auth";
import { currentAccount, login as msalLogin, logout as msalLogout } from "../lib/msal";
import { devSignIn } from "../lib/dev-auth";
import { apiFetch, errorMessage } from "../lib/api";
import { authConfigQuery, queryKeys } from "../lib/queries";
import { Alert, Button, Input } from "../components/ui";

const searchSchema = z.object({
  redirect: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    // Already signed in? Don't show the button again.
    const user = await fetchUser(context.queryClient);
    if (user) throw redirect({ to: homeFor(user.role) });
  },
  component: LoginPage,
});

/**
 * Film grain, as a data URI.
 *
 * Inline rather than a file in `public/`: the Static Web App serves this page
 * before anything else, and a decorative texture is not worth a second round
 * trip on the one screen where nothing is cached yet. `feTurbulence` is also
 * resolution-independent, so it does not band on a 4K panel the way a tiled PNG
 * would.
 */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")";

const HIGHLIGHTS = [
  "Weekly timesheets, submitted and approved in one place",
  "Effective-dated rates, frozen the moment a week is approved",
  "Clients, projects and contractors in one workspace",
];

/**
 * The Microsoft mark.
 *
 * Microsoft's own branding guidance for "Sign in with Microsoft" asks for this
 * logo rather than a generic glyph — the button used a `Globe`, which reads as
 * "some website" on the one control a user has to trust. Hard-coded brand hexes
 * on purpose: these four are Microsoft's, not ours, and must not follow our
 * theme.
 */
function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" className="h-4 w-4 shrink-0" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function LoginPage() {
  const search = Route.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: authConfig } = useQuery(authConfigQuery);
  const [signingIn, setSigningIn] = useState(false);
  const [devEmail, setDevEmail] = useState("");
  const [devError, setDevError] = useState<string | null>(null);

  const configured = authConfig?.configured === true;
  const devSignin = import.meta.env.DEV && authConfig?.devSignin === true;
  // An Entra session with no local access — the "signed in but 401" dead end.
  const stranded = configured ? currentAccount() : null;

  const signIn = () => {
    setSigningIn(true);
    // Redirects away; no need to await. On failure the page simply stays.
    void msalLogin().catch(() => setSigningIn(false));
  };

  const signInDev = async () => {
    setSigningIn(true);
    setDevError(null);
    try {
      await devSignIn(devEmail);
      const { user } = await apiFetch("GET /api/me");
      queryClient.setQueryData(queryKeys.me, { user });
      await router.navigate({ to: homeFor(user.role) });
    } catch (err) {
      setDevError(errorMessage(err));
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas lg:grid lg:grid-cols-[1.1fr_1fr]">
      {/*
        The brand panel is always a dark accent field, in either theme, so its
        contents are the one place raw ramp steps are correct: semantic tokens
        would flip `text-*` to dark ink against a permanently dark background.
        Same reasoning as the overlay scrims.
      */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-accent-950 p-12 text-white lg:flex xl:p-16">
        {/* The mesh: three wide, heavily blurred fields rather than one linear
            ramp, so the panel has depth instead of a gradient's flat diagonal. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{
            backgroundImage: [
              "radial-gradient(60rem 40rem at 12% 8%, var(--color-accent-600), transparent 60%)",
              "radial-gradient(45rem 45rem at 92% 22%, var(--color-accent-500), transparent 55%)",
              "radial-gradient(50rem 35rem at 68% 104%, var(--color-accent-700), transparent 60%)",
            ].join(","),
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.14] mix-blend-overlay"
          style={{ backgroundImage: GRAIN }}
        />
        {/* A hairline of light along the seam, so the panel ends deliberately
            rather than just stopping. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-white/15"
        />

        <div className="relative z-10 motion-safe:animate-fade-in">
          <img
            src="/simplok-logo.png"
            alt="Simplok"
            /* The wordmark is a deep-blue PNG on transparency, so on this panel
               it needed a white chip behind it to be visible at all — a box
               bolted onto the brand. `brightness-0 invert` drives every opaque
               pixel to white and leaves the alpha alone, which is the wordmark
               reversed out, as it should be on a dark field. */
            className="h-8 w-auto brightness-0 invert"
          />
        </div>

        <div className="relative z-10 motion-safe:animate-slide-up">
          <h2 className="max-w-xl text-3xl font-semibold leading-tight xl:text-4xl">
            Run your contractor operations on autopilot.
          </h2>
          <p className="mt-4 max-w-md text-accent-200">
            Track timesheets, review and approve weekly hours, and keep your contractor
            delivery on top of everything — in one clean workspace built for IT consulting
            teams.
          </p>

          <ul className="mt-8 space-y-3">
            {HIGHLIGHTS.map((line) => (
              <li key={line} className="flex items-start gap-3 text-sm text-accent-100">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/15 ring-1 ring-inset ring-white/25">
                  <Check className="h-3 w-3" aria-hidden="true" strokeWidth={3} />
                </span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 text-xs text-accent-300">
          © 2026 Simplok Pty Ltd. All rights reserved.
        </div>
      </div>

      <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-6 sm:p-12 lg:min-h-0">
        {/* Something for the card's backdrop-blur to actually blur. Without a
            field behind it, frosted glass is just a flat panel. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-accent-soft blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-accent-soft blur-3xl"
        />

        <div className="relative w-full max-w-md motion-safe:animate-scale-in">
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <img src="/simplok-logo.png" alt="Simplok" className="h-7 w-auto" />
            <span className="text-sm font-medium text-subtle">ContractorHub</span>
          </div>

          <div className="rounded-2xl border border-line bg-surface-raised/85 p-7 shadow-e4 backdrop-blur-xl sm:p-8">
            <h1 className="text-2xl font-semibold text-text">Sign in to ContractorHub</h1>
            <p className="mt-1.5 text-sm text-subtle">
              Use your Simplok account to continue.
            </p>

            {search.error === "activation" ? (
              <div className="mt-6">
                <Alert tone="error">
                  We couldn&apos;t activate your invitation. Make sure you signed in with
                  the email address it was sent to, then try the link again.
                </Alert>
              </div>
            ) : stranded ? (
              // Signed in at Entra, but /me said no: reaching this page at all means
              // the account has no active local user (invite expired or revoked, or a
              // stranger's Microsoft account). Without this alert — and the sign-out
              // escape below — clicking "Sign in" just silently SSO-loops back here.
              <div className="mt-6">
                <Alert tone="warn">
                  You&apos;re signed in with Microsoft as{" "}
                  <strong>{stranded.username}</strong>, but that account doesn&apos;t have
                  access yet. Your invitation may be expired or revoked — ask your
                  administrator to re-send it, or sign out to use a different account.
                </Alert>
              </div>
            ) : null}

            {authConfig && !configured ? (
              <div className="mt-7">
                <Alert tone="error">
                  Sign-in isn&apos;t configured for this environment yet. Please contact
                  your administrator.
                </Alert>
              </div>
            ) : (
              <Button
                size="lg"
                fullWidth
                className="mt-7"
                loading={signingIn}
                disabled={!configured}
                onClick={signIn}
              >
                {!signingIn && <MicrosoftMark />}
                Sign in with Microsoft
              </Button>
            )}

            {stranded && (
              <Button
                variant="link"
                fullWidth
                className="mt-3 justify-center py-2"
                onClick={() => void msalLogout()}
              >
                Sign out of {stranded.username}
              </Button>
            )}

            {devSignin && (
              <div className="mt-6 rounded-lg border border-dashed border-line bg-surface-sunken p-3">
                <p className="mb-2 text-xs font-medium text-warning-on-soft">
                  Dev sign-in (non-production)
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    aria-label="Dev sign-in email"
                    value={devEmail}
                    onChange={(e) => setDevEmail(e.target.value)}
                    placeholder="email to sign in as"
                  />
                  <Button
                    variant="secondary"
                    disabled={!devEmail || signingIn}
                    onClick={() => void signInDev()}
                  >
                    Go
                  </Button>
                </div>
                {devError && (
                  <p className="mt-2 text-xs text-danger-on-soft">{devError}</p>
                )}
              </div>
            )}
          </div>

          {/*
            The legal line sits outside the card and below a rule, because it is
            a footnote about the product rather than a step in signing in. It
            names two documents that are not published anywhere yet, so nothing
            here is a link — a link that goes nowhere is worse than plain text.
          */}
          <p className="mx-auto mt-6 max-w-xs border-t border-line pt-5 text-center text-xs leading-relaxed text-subtle">
            By signing in you agree to Simplok&apos;s Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
